<template>
  <aside class="new-validity-panel">
    <header class="new-validity-panel__header">
      <h3 class="new-validity-panel__title">Nova vigência</h3>

      <a-button
        class="new-validity-panel__close"
        type="link"
        aria-label="Fechar nova vigência"
        @click="$emit('close')"
      >
        <a-icon type="close" />
      </a-button>
    </header>

    <div class="new-validity-panel__body">
      <section class="new-validity-panel__route">
        <p class="new-validity-panel__route-title">
          {{ route.nome }}: <strong>{{ route.destino }}</strong>
        </p>
        <p v-if="metaParts.length" class="new-validity-panel__route-meta">
          <template v-for="(part, index) in metaParts">
            <span
              v-if="index > 0"
              :key="`sep-${index}`"
              class="new-validity-panel__separator"
            >
              •
            </span>
            <span :key="index">{{ part }}</span>
          </template>
        </p>
      </section>

      <section class="new-validity-panel__locality">
        <div class="new-validity-panel__field">
          <span class="new-validity-panel__label">Município origem</span>
          <a-input :value="route.origem" disabled />
        </div>

        <div class="new-validity-panel__field">
          <span class="new-validity-panel__label">
            {{ route.roteiro ? "Roteiro" : "Destino" }}
          </span>
          <a-select
            v-if="route.roteiro"
            :value="route.roteiro"
            :show-arrow="true"
            disabled
          />
          <a-input v-else :value="route.destinoLocal" disabled />
        </div>
      </section>

      <section class="new-validity-panel__validity">
        <div class="new-validity-panel__section-head">
          <h4 class="new-validity-panel__section-title">Vigência</h4>
          <a-tooltip :title="canAddBlock ? '' : MAX_BLOCKS_MESSAGE" placement="topRight">
            <a-button
              type="link"
              class="new-validity-panel__add-block"
              :disabled="!canAddBlock"
              @click="addBlock"
            >
              <a-icon type="plus" /> Nova vigência
            </a-button>
          </a-tooltip>
        </div>

        <a-alert
          v-if="blockedMessage"
          class="new-validity-panel__alert"
          type="warning"
          :message="blockedMessage"
          show-icon
        />

        <a-alert
          v-if="willInactivateRoute"
          class="new-validity-panel__alert"
          type="warning"
          :message="ROUTE_INACTIVE_MESSAGE"
          show-icon
        />

        <a-alert
          v-for="message in errors.geral"
          :key="message"
          class="new-validity-panel__alert"
          type="error"
          :message="message"
          show-icon
        />

        <div
          v-for="(bloco, blockIndex) in form.blocos"
          :key="bloco.key"
          class="new-validity-panel__block"
        >
          <div v-if="form.blocos.length > 1" class="new-validity-panel__block-head">
            <span class="new-validity-panel__block-title">
              Vigência {{ blockIndex + 1 }}
            </span>
            <a-button
              v-if="blockIndex > 0"
              type="link"
              class="new-validity-panel__trash"
              :aria-label="`Remover vigência ${blockIndex + 1}`"
              @click="removeBlock(blockIndex)"
            >
              <a-icon type="delete" />
            </a-button>
          </div>

          <div
            class="new-validity-panel__field"
            :class="{
              'new-validity-panel__field--error': periodErrors(blockIndex).length,
            }"
          >
            <span class="new-validity-panel__label">
              <span class="new-validity-panel__required">*</span> Periodo
            </span>

            <a-range-picker
              v-if="editingPeriod === blockIndex"
              :open="true"
              :value="periodRange(bloco)"
              :locale="datePickerLocale"
              :format="DATE_FORMAT"
              :disabled-date="isBeforeToday"
              :placeholder="['Data inicial', 'Data final']"
              separator="→"
              :allow-clear="false"
              @calendarChange="(dates) => onPeriodCalendarChange(blockIndex, dates)"
              @change="(dates) => onPeriodChange(blockIndex, dates)"
              @openChange="onPeriodOpenChange"
            />
            <div
              v-else
              class="new-validity-panel__period"
              role="button"
              tabindex="0"
              @click="openPeriod(blockIndex)"
              @keydown.enter.prevent="openPeriod(blockIndex)"
              @keydown.space.prevent="openPeriod(blockIndex)"
            >
              <a-icon type="calendar" class="new-validity-panel__period-icon" />
              <template v-if="bloco.inicio">
                <span
                  v-for="campo in PERIOD_FIELDS"
                  v-show="bloco[campo]"
                  :key="campo"
                  class="new-validity-panel__period-tag"
                >
                  {{ bloco[campo] }}
                  <a-icon
                    type="close"
                    :aria-label="`Remover ${bloco[campo]}`"
                    @click.stop="clearPeriodDate(blockIndex, campo)"
                  />
                </span>
              </template>
              <span v-else class="new-validity-panel__placeholder">
                Informe uma data de vigência
              </span>
            </div>

            <p
              v-for="message in periodErrors(blockIndex)"
              :key="message"
              class="new-validity-panel__error"
            >
              {{ message }}
            </p>
          </div>

          <a-alert
            v-for="message in blockErrors(blockIndex).campo"
            :key="message"
            class="new-validity-panel__alert"
            type="error"
            :message="message"
            show-icon
          />

          <template v-if="mode === MODES.VEHICLES">
            <div
              v-for="(linha, index) in bloco.linhas"
              :key="index"
              class="new-validity-panel__line"
            >
              <div
                class="new-validity-panel__field"
                :class="{
                  'new-validity-panel__field--error': lineError(blockIndex, index, 'valor'),
                }"
              >
                <span class="new-validity-panel__label">
                  <span class="new-validity-panel__required">*</span> Valor frete
                </span>
                <a-input
                  :value="formatCurrency(linha.vlr)"
                  placeholder="Informe o valor do frete"
                  @change="(e) => onValueChange(blockIndex, index, e)"
                />
                <p
                  v-if="lineError(blockIndex, index, 'valor')"
                  class="new-validity-panel__error"
                >
                  {{ lineError(blockIndex, index, "valor") }}
                </p>
              </div>

              <div
                class="new-validity-panel__field"
                :class="{
                  'new-validity-panel__field--error': lineError(blockIndex, index, 'veiculo'),
                }"
              >
                <span class="new-validity-panel__label">
                  <span class="new-validity-panel__required">*</span> Veiculo
                </span>
                <FreightTableVehicleSelect
                  :value="linha.tpRodados"
                  :options="vehicleOptionsFor(bloco, index)"
                  :placeholder="vehiclesLoading ? 'Carregando veículos...' : 'Selecione o veículo'"
                  :max-tags="1"
                  @change="(ids) => onVehicleChange(blockIndex, index, ids)"
                />
                <p
                  v-if="lineError(blockIndex, index, 'veiculo')"
                  class="new-validity-panel__error"
                >
                  {{ lineError(blockIndex, index, "veiculo") }}
                </p>
              </div>

              <div class="new-validity-panel__line-actions">
                <a-tooltip
                  :title="canAddLine(bloco) ? '' : NO_VEHICLE_LEFT_MESSAGE"
                  placement="topRight"
                >
                  <a-button
                    class="new-validity-panel__add"
                    aria-label="Adicionar veículo"
                    :disabled="!canAddLine(bloco)"
                    @click="addLine(blockIndex)"
                  >
                    <a-icon type="plus" />
                  </a-button>
                </a-tooltip>
                <a-button
                  v-if="bloco.linhas.length > 1"
                  type="link"
                  class="new-validity-panel__trash"
                  aria-label="Remover veículo"
                  @click="removeLine(blockIndex, index)"
                >
                  <a-icon type="delete" />
                </a-button>
              </div>
            </div>
          </template>

          <FreightTableNewValidityRanges
            v-else-if="mode === MODES.RANGES"
            :value="bloco.faixas"
            :errors="rangeErrors(blockIndex)"
            :title="rangeTitle"
            @update:value="(faixas) => onRangesChange(blockIndex, faixas)"
          />

          <div
            v-else-if="mode === MODES.VALUE"
            class="new-validity-panel__field"
            :class="{ 'new-validity-panel__field--error': valueError(blockIndex) }"
          >
            <span class="new-validity-panel__label">
              <span class="new-validity-panel__required">*</span> {{ valueLabel }}
            </span>
            <a-input
              v-if="percentual"
              :value="formatDecimal(bloco.vlr, 2)"
              placeholder="Informe o percentual"
              suffix="%"
              @change="(e) => onValueFieldChange(blockIndex, e)"
            />
            <a-input
              v-else
              :value="formatCurrency(bloco.vlr)"
              placeholder="Informe o valor do frete"
              @change="(e) => onValueFieldChange(blockIndex, e)"
            />
            <p v-if="valueError(blockIndex)" class="new-validity-panel__error">
              {{ valueError(blockIndex) }}
            </p>
          </div>

          <div v-else-if="mode === MODES.COMMISSION" class="new-validity-panel__commission">
            <div
              v-for="field in COMMISSION_FIELDS"
              :key="field.key"
              class="new-validity-panel__field"
              :class="{
                'new-validity-panel__field--error': commissionError(blockIndex, field.key),
              }"
            >
              <span class="new-validity-panel__label">{{ field.label }}</span>
              <a-input
                :value="formatCommission(bloco.comissao[field.key], field)"
                :placeholder="field.placeholder"
                :suffix="field.percent ? '%' : undefined"
                @change="(e) => onCommissionChange(blockIndex, field, e)"
              />
              <p
                v-if="commissionError(blockIndex, field.key)"
                class="new-validity-panel__error"
              >
                {{ commissionError(blockIndex, field.key) }}
              </p>
            </div>

            <div
              class="new-validity-panel__field new-validity-panel__field--wide"
              :class="{
                'new-validity-panel__field--error': commissionError(blockIndex, 'custoId'),
              }"
            >
              <span class="new-validity-panel__label">
                <span class="new-validity-panel__required">*</span> Custo da comissão
              </span>
              <a-select
                :value="bloco.comissao.custoId === null ? undefined : bloco.comissao.custoId"
                placeholder="Selecione o custo"
                show-search
                option-filter-prop="children"
                @change="(value) => onCommissionCostChange(blockIndex, value)"
              >
                <a-select-option
                  v-for="option in costs"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </a-select-option>
              </a-select>
              <p
                v-if="commissionError(blockIndex, 'custoId')"
                class="new-validity-panel__error"
              >
                {{ commissionError(blockIndex, "custoId") }}
              </p>
            </div>

            <p class="new-validity-panel__hint">
              Informe o valor fixo ou a taxa.
            </p>
          </div>

          <FreightTableRouteTaxes
            class="new-validity-panel__taxes"
            compact
            :value="bloco.taxas"
            :errors="taxErrors(blockIndex)"
            :excluded-trechos="excludedTrechos"
            :status="switchStatus"
            :status-error="blockIndex === 0 ? errors.rotaStatus || '' : ''"
            @status-change="onRouteStatusChange"
            @patch="(partial) => patchTaxas(blockIndex, partial)"
          />
        </div>
      </section>
    </div>

    <footer class="new-validity-panel__footer">
      <a-button :disabled="saving" @click="$emit('close')">Cancelar</a-button>
      <a-button
        type="primary"
        class="new-validity-panel__save"
        :disabled="!canSave"
        :loading="saving"
        @click="submit"
      >
        Salvar
      </a-button>
    </footer>
  </aside>
</template>

<script>
import moment from "moment";
import "moment/locale/pt-br";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";
import FreightTableVehicleSelect from "../Create/FreightTableVehicleSelect.vue";
import FreightTableNewValidityRanges from "./FreightTableNewValidityRanges.vue";
import FreightTableRouteTaxes from "../Create/FreightTableRouteTaxes.vue";
import { FREIGHT_TABLE_STATUS } from "../../constants/freightTableStatus";
import {
  NEW_VALIDITY_MAX_BLOCKS,
  NEW_VALIDITY_MODE,
  PERCENTUAL_NF_MAX,
  TAXA_COMISSAO_MAX,
  createEmptyNewValidityBlock,
  createEmptyNewValidityLine,
  emptyNewValidityBlockErrors,
  emptyNewValidityErrors,
  excludedTrechosFor,
  findNewValidityBlockConflicts,
  findNewValidityCostIssues,
  findRangeIssues,
  hasAvailableVehicle,
  isNewValidityComplete,
  isPercentualCalc,
  newValidityDefaults,
  newValidityModeFor,
  selectedVehicleIds,
} from "../../utils/newValidityForm";
import {
  FREIGHT_TABLE_CALC_FAIXA_PESO_VEICULO,
  describeRangeBlockTitle,
} from "../../constants/freightTableOptions";

const DATE_FORMAT = "DD/MM/YYYY";
const PERIOD_FIELDS = ["inicio", "fim"];
const NO_VEHICLE_LEFT_MESSAGE =
  "Todos os veículos já foram selecionados nesta vigência. Para usar outro valor, remova um veículo de uma das linhas.";
const MAX_BLOCKS_MESSAGE = `É possível incluir até ${NEW_VALIDITY_MAX_BLOCKS} vigências de uma vez.`;

const NO_ROUTE_VEHICLE_MESSAGE = "Cadastre um veículo na rota antes de incluir vigência.";

const ROUTE_INACTIVE_MESSAGE =
  "A rota ficará inativa e não será usada no cálculo de frete.";

const COMMISSION_FIELDS = [
  { key: "vlrTaxa", label: "Valor fixo", placeholder: "R$ 0,00" },
  { key: "taxaComissao", label: "Taxa", placeholder: "0,000", percent: true, decimals: 3 },
  { key: "vlrMinimo", label: "Valor mínimo", placeholder: "R$ 0,00" },
];

let blockKeySeq = 0;

function newBlock(context) {
  blockKeySeq += 1;
  return {
    ...createEmptyNewValidityBlock(newValidityDefaults(context)),
    key: blockKeySeq,
  };
}

export default {
  name: "FreightTableNewValidityPanel",
  components: {
    FreightTableVehicleSelect,
    FreightTableNewValidityRanges,
    FreightTableRouteTaxes,
  },
  props: {
    route: {
      type: Object,
      required: true,
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    vehiclesLoading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: emptyNewValidityErrors,
    },
    tpCalc: {
      type: Number,
      default: null,
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    costs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      DATE_FORMAT,
      PERIOD_FIELDS,
      NO_VEHICLE_LEFT_MESSAGE,
      MAX_BLOCKS_MESSAGE,
      ROUTE_INACTIVE_MESSAGE,
      COMMISSION_FIELDS,
      MODES: NEW_VALIDITY_MODE,
      datePickerLocale: ptBR,
      form: { rotaStatus: null, blocos: [newBlock(this.context)] },
      editingPeriod: null,
      periodEndChosen: false,
    };
  },
  computed: {
    metaParts() {
      const paradas = this.route.paradas || [];
      if (paradas.length) {
        return [`${paradas.length} paradas`, paradas.join(" - ")];
      }

      return this.route.meta || [];
    },
    blockConflicts() {
      return findNewValidityBlockConflicts(this.form);
    },
    canAddBlock() {
      return this.form.blocos.length < NEW_VALIDITY_MAX_BLOCKS;
    },
    mode() {
      return newValidityModeFor(this.tpCalc);
    },
    routeStatus() {
      const status = Number(this.route.statusValor);
      return status ? status : FREIGHT_TABLE_STATUS.ATIVA;
    },
    switchStatus() {
      return this.form.rotaStatus === null ? this.routeStatus : this.form.rotaStatus;
    },
    willInactivateRoute() {
      return (
        this.switchStatus === FREIGHT_TABLE_STATUS.INATIVA &&
        this.routeStatus !== FREIGHT_TABLE_STATUS.INATIVA
      );
    },
    excludedTrechos() {
      return excludedTrechosFor(this.tpCalc);
    },
    percentual() {
      return isPercentualCalc(this.tpCalc);
    },
    valueLabel() {
      return this.percentual ? "Percentual" : "Valor frete";
    },
    rangeTitle() {
      return describeRangeBlockTitle(this.tpCalc);
    },
    blockedMessage() {
      if (
        Number(this.tpCalc) === FREIGHT_TABLE_CALC_FAIXA_PESO_VEICULO &&
        this.context.hasActiveVehicle === false
      ) {
        return NO_ROUTE_VEHICLE_MESSAGE;
      }
      return "";
    },
    canSave() {
      return (
        !this.saving &&
        !this.blockedMessage &&
        isNewValidityComplete(this.form, this.tpCalc)
      );
    },
  },
  watch: {
    "route.routeId"() {
      this.form = { rotaStatus: null, blocos: [newBlock(this.context)] };
      this.editingPeriod = null;
    },
  },
  methods: {
    toMoment(value) {
      if (!value) return null;
      const parsed = moment(value, DATE_FORMAT, true);
      return parsed.isValid() ? parsed : null;
    },
    periodRange(bloco) {
      const inicio = this.toMoment(bloco.inicio);
      const fim = this.toMoment(bloco.fim);
      if (!inicio && !fim) return [];
      return [inicio, fim];
    },
    isBeforeToday(current) {
      return !!current && current.isBefore(moment().startOf("day"));
    },
    blockErrors(blockIndex) {
      return this.errors.blocos[blockIndex] || emptyNewValidityBlockErrors();
    },
    rangeErrors(blockIndex) {
      const local = findRangeIssues(this.form.blocos[blockIndex].faixas, this.tpCalc);
      const server = this.blockErrors(blockIndex).faixas;
      const merged = {};
      Object.keys(local)
        .concat(Object.keys(server))
        .forEach((index) => {
          merged[index] = { ...(local[index] || {}), ...(server[index] || {}) };
        });
      return merged;
    },
    taxErrors(blockIndex) {
      const server = this.blockErrors(blockIndex);
      const local = findNewValidityCostIssues(this.form.blocos[blockIndex], this.tpCalc);
      const custos = {};
      Object.keys(local)
        .concat(Object.keys(server.custos))
        .forEach((index) => {
          custos[index] = { ...(local[index] || {}), ...(server.custos[index] || {}) };
        });
      return { taxas: server.taxas, custos };
    },
    onRouteStatusChange(status) {
      this.form = {
        ...this.form,
        rotaStatus: status === this.routeStatus ? null : status,
      };
      this.$emit("edit");
    },
    patchTaxas(blockIndex, partial) {
      const bloco = this.form.blocos[blockIndex];
      this.patchBlock(blockIndex, { taxas: { ...bloco.taxas, ...partial } });
    },
    valueError(blockIndex) {
      const server = this.blockErrors(blockIndex).valor;
      if (server) return server;
      const vlr = this.form.blocos[blockIndex].vlr;
      if (this.percentual && vlr !== null && Number(vlr) > PERCENTUAL_NF_MAX) {
        return "O percentual não pode passar de 100.";
      }
      return "";
    },
    commissionError(blockIndex, field) {
      const server = this.blockErrors(blockIndex).comissao[field];
      if (server) return server;
      const taxa = this.form.blocos[blockIndex].comissao.taxaComissao;
      if (field === "taxaComissao" && taxa !== null && Number(taxa) > TAXA_COMISSAO_MAX) {
        return "A taxa não pode passar de 99,999%.";
      }
      return "";
    },
    formatDecimal(value, decimals) {
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      return number.toLocaleString("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    formatCommission(value, field) {
      return field.percent
        ? this.formatDecimal(value, field.decimals)
        : this.formatCurrency(value);
    },
    readScaled(e, decimals) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      return digits === "" ? null : Number(digits) / Math.pow(10, decimals);
    },
    onRangesChange(blockIndex, faixas) {
      this.patchBlock(blockIndex, { faixas });
    },
    onValueFieldChange(blockIndex, e) {
      this.patchBlock(blockIndex, { vlr: this.readScaled(e, 2) });
    },
    onCommissionChange(blockIndex, field, e) {
      const bloco = this.form.blocos[blockIndex];
      this.patchBlock(blockIndex, {
        comissao: {
          ...bloco.comissao,
          [field.key]: this.readScaled(e, field.decimals || 2),
        },
      });
    },
    onCommissionCostChange(blockIndex, value) {
      const bloco = this.form.blocos[blockIndex];
      this.patchBlock(blockIndex, {
        comissao: {
          ...bloco.comissao,
          custoId: value === undefined || value === null ? null : Number(value),
        },
      });
    },
    periodErrors(blockIndex) {
      const messages = this.blockErrors(blockIndex).periodo.slice();
      const conflict = this.blockConflicts[blockIndex];
      if (conflict && messages.indexOf(conflict) === -1) messages.push(conflict);
      return messages;
    },
    lineError(blockIndex, index, field) {
      const line = this.blockErrors(blockIndex).linhas[index];
      return line ? line[field] || "" : "";
    },
    vehicleOptionsFor(bloco, index) {
      const taken = selectedVehicleIds(bloco.linhas, index);
      return this.vehicles.filter(
        (option) => taken.indexOf(Number(option.value)) === -1,
      );
    },
    canAddLine(bloco) {
      return hasAvailableVehicle(bloco.linhas, this.vehicles);
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      return number.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    readDigits(e) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      return digits === "" ? null : Number(digits) / 100;
    },
    setBlocks(blocos) {
      this.form = { ...this.form, blocos };
      this.$emit("edit");
    },
    patchBlock(blockIndex, patch) {
      this.setBlocks(
        this.form.blocos.map((bloco, i) =>
          i === blockIndex ? { ...bloco, ...patch } : bloco,
        ),
      );
    },
    patchLine(blockIndex, index, patch) {
      const bloco = this.form.blocos[blockIndex];
      this.patchBlock(blockIndex, {
        linhas: bloco.linhas.map((line, i) =>
          i === index ? { ...line, ...patch } : line,
        ),
      });
    },
    onValueChange(blockIndex, index, e) {
      this.patchLine(blockIndex, index, { vlr: this.readDigits(e) });
    },
    onVehicleChange(blockIndex, index, ids) {
      this.patchLine(blockIndex, index, { tpRodados: (ids || []).map(Number) });
    },
    addLine(blockIndex) {
      const bloco = this.form.blocos[blockIndex];
      if (!this.canAddLine(bloco)) return;
      this.patchBlock(blockIndex, {
        linhas: bloco.linhas.concat([createEmptyNewValidityLine()]),
      });
    },
    removeLine(blockIndex, index) {
      const bloco = this.form.blocos[blockIndex];
      if (bloco.linhas.length <= 1) return;
      this.patchBlock(blockIndex, {
        linhas: bloco.linhas.filter((_, i) => i !== index),
      });
    },
    addBlock() {
      if (!this.canAddBlock) return;
      this.setBlocks(this.form.blocos.concat([newBlock(this.context)]));
    },
    removeBlock(blockIndex) {
      if (blockIndex === 0) return;
      this.editingPeriod = null;
      this.setBlocks(this.form.blocos.filter((_, i) => i !== blockIndex));
    },
    openPeriod(blockIndex) {
      this.editingPeriod = blockIndex;
      this.periodEndChosen = false;
    },
    onPeriodCalendarChange(blockIndex, dates) {
      const [inicio, fim] = dates || [];
      if (!inicio) return;

      this.periodEndChosen = !!fim;
      this.patchBlock(blockIndex, {
        inicio: inicio.format(DATE_FORMAT),
        fim: fim ? fim.format(DATE_FORMAT) : null,
      });
    },
    onPeriodChange(blockIndex, dates) {
      const [inicio, fim] = dates || [];
      const closedRange = this.periodEndChosen && !!fim;

      this.patchBlock(blockIndex, {
        inicio: inicio ? inicio.format(DATE_FORMAT) : null,
        fim: closedRange ? fim.format(DATE_FORMAT) : null,
      });
      this.editingPeriod = null;
    },
    onPeriodOpenChange(open) {
      if (!open) this.editingPeriod = null;
    },
    clearPeriodDate(blockIndex, campo) {
      this.patchBlock(
        blockIndex,
        campo === "inicio" ? { inicio: null, fim: null } : { fim: null },
      );
    },
    submit() {
      if (!this.canSave) return;
      this.$emit("submit", this.form);
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.new-validity-panel {
  position: sticky;
  top: 16px;
  width: 100%;
  height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: @color-bg-panel;
  border: 1px solid @color-border-light;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  font-family: @font-primary;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 20px 16px;
    border-bottom: 1px solid @color-border-light;
  }

  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    color: @color-text-dark;
  }

  &__close {
    color: @color-text-light;
  }

  &__body {
    flex: 1;
    min-height: 0;
    padding: 0 10px 16px 10px;
    overflow-y: auto;
  }

  &__route,
  &__locality {
    padding: 16px 0;
    border-bottom: 1px solid @color-border-light;
  }

  &__route-title {
    margin: 0 0 4px 0;
    font-size: 14px;
    color: @color-text-medium;

    strong {
      font-weight: 600;
      color: @color-text-dark;
    }
  }

  &__route-meta {
    margin: 0;
    font-size: 12px;
    color: @color-text-light;
  }

  &__separator {
    margin: 0 6px;
  }

  &__locality {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    .new-validity-panel__field {
      margin-bottom: 0;
    }
  }

  &__validity {
    padding-top: 24px;
  }

  &__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__add-block {
    padding: 0 8px;
    color: @color-primary;

    &:hover,
    &:focus {
      color: @color-primary-hover;
    }
  }

  &__block {
    & + & {
      margin-top: 8px;
      padding-top: 16px;
      border-top: 1px solid @color-border-light;
    }
  }

  &__block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    margin-bottom: 8px;
  }

  &__block-title {
    font-size: 14px;
    font-weight: 500;
    color: @color-text-dark;
  }

  &__section-title {
    margin: 0 0 0 8px;
    font-size: 16px;
    font-weight: 500;
    color: @color-text-dark;
  }

  &__alert {
    margin-bottom: 12px;
  }

  &__commission {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0 12px;
  }

  &__taxes {
    margin-bottom: 16px;
    padding: 0 8px;
  }

  &__field--wide,
  &__hint {
    grid-column: ~"1 / -1";
  }

  &__hint {
    margin: 0 0 16px 0;
    font-size: 12px;
    line-height: 18px;
    color: @color-text-light;
  }

  &__field {
    display: flex;
    flex-direction: column;
    min-width: 0;
    margin-bottom: 16px;

    /deep/ .ant-select,
    /deep/ .ant-calendar-picker {
      width: 100%;
    }

    &--error {
      /deep/ .ant-input,
      /deep/ .ant-select-selection,
      /deep/ .freight-vehicle-select__trigger,
      .new-validity-panel__period {
        border-color: @color-danger;
      }
    }
  }

  &__label {
    display: block;
    padding-bottom: 8px;
    font-size: 14px;
    line-height: 22px;
    color: @color-text-dark;
  }

  &__required {
    margin-right: 2px;
    color: @color-danger;
  }

  &__period {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    box-sizing: border-box;
    min-height: 32px;
    padding: 3px 8px;
    background: @color-bg-surface;
    border: 1px solid @color-border-input;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.3s;

    &:hover,
    &:focus {
      border-color: @color-primary;
      outline: none;
    }
  }

  &__period-icon {
    color: @color-text-light;
  }

  &__period-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 8px;
    background: @color-tag-info-bg;
    border-radius: 4px;
    font-size: 14px;
    line-height: 22px;
    color: @color-primary;
    white-space: nowrap;

    .anticon {
      font-size: 12px;
      color: @color-text-light;
      cursor: pointer;

      &:hover {
        color: @color-text-medium;
      }
    }
  }

  &__placeholder {
    font-size: 14px;
    line-height: 22px;
    color: @color-text-muted;
  }

  &__line {
    display: grid;
    grid-template-columns: 1fr 1fr 72px;
    gap: 8px;
    align-items: start;

    /deep/ .freight-vehicle-select__trigger {
      height: 32px;
    }
  }

  &__line-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-top: 22px;
  }

  &__add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 6px;
    color: @color-text-dark;
  }

  &__trash {
    padding: 0;
    color: @color-danger;
  }

  &__error {
    margin: 4px 0 0 0;
    white-space: pre-line;
    font-size: 12px;
    line-height: 18px;
    color: @color-danger;
  }

  &__footer {
    display: flex;
    gap: 8px;
    padding: 16px;
  }

  &__save:not([disabled]) {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }
}
</style>
